import * as React from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import { Cabinet, ICabinet, CabinetTopology, CabinetDevicePayload } from '../../models/cabinet';
import PucEditor from '../puc-editor';
import { getCabinets, getCabinetConfiguration, upsertCabinet, putCabinetDevice, deleteCabinetDevice, getCabinetTopology, updateCabinetTopology, lookupDeviceTopology } from '../../utilities/api-utils';
import store, { useAppSelector } from '../../store';
import allCountries from '../../utilities/all-countries';
import DeviceRosterFields, { DeviceRow, isDeviceRosterValid } from '../device-roster-fields';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Tabs,
  Tab,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  SelectChangeEvent,
  OutlinedInput,
  MenuItem
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Puc } from '../../models/puc';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function CabinetTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cabinet-tabpanel-${index}`}
      aria-labelledby={`cabinet-tab-${index}`}
      {...other}>
      {value === index && (
        <Box sx={{ marginTop: '20px', marginLeft: '20px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `cabinet-tab-${index}`,
    'aria-controls': `cabinet-tabpanel-${index}`,
  };
}

interface CabinetDialogProps {
  editingCabinet: string,
  selectedStoreId: string,
  setDialog(arg: boolean): void,
  refresh(): void
}

function getStyles(country: string, smsCountries: string[], theme: Theme) {
  return {
    fontWeight:
      smsCountries.indexOf(country) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

function CabinetDialog({ editingCabinet, selectedStoreId, setDialog, refresh }: CabinetDialogProps) {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  
  const theme = useTheme();

  const { stores } = useAppSelector((store) => store.store);
  const [storeIdErrorText, setStoreIdErrorText] = useState<string>('');
  const [descriptionErrorText, setDescriptionErrorText] = useState<string>('');
  const [businessNameErrorText, setBusinessNameErrorText] = useState<string>('');
  const [businessAddressErrorText, setBusinessAddressErrorText] = useState<string>('');
  const [businessPhoneErrorText, setBusinessPhoneErrorText] = useState<string>('');
  const [cabinetLocationErrorText, setCabinetLocationErrorText] = useState<string>('');
  const [brandErrorText, setBrandErrorText] = useState<string>('');
  const [orderManagerCodeErrorText, setOrderManagerCodeErrorText] = useState<string>('');
  const [hasStoreIdError, setHasStoreIdError] = useState<boolean>(false);
  const [hasDescriptionError, setHasDescriptionError] = useState<boolean>(false);
  const [hasBusinessNameError, setHasBusinessNameError] = useState<boolean>(false);
  const [hasBusinessAddressError, setHasBusinessAddressError] = useState<boolean>(false);
  const [hasBusinessPhoneError, setHasBusinessPhoneError] = useState<boolean>(false);
  const [hasCabinetLocationError, setHasCabinetLocationError] = useState<boolean>(false);
  const [hasBrandError, setHasBrandError] = useState<boolean>(false);
  const [hasOrderManagerCodeError, setHasOrderManagerCodeError] = useState<boolean>(false);
  const [intermediateStoreId, setIntermediateStoreId] = useState<string>('');
  const [cabinetObject, setCabinetObject] = useState<Cabinet>();
  const [storeId, setStoreId] = useState<string>('');
  const [cabinetId, setCabinetId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [businessAddress, setBusinessAddress] = useState<string>('');
  const [businessPhone, setBusinessPhone] = useState<string>('');
  const [cabinetLocation, setCabinetLocation] = useState<string>('');
  const [hardwareConfig, setHardwareConfig] = useState<Array<Puc>>([]);
  const [smsCountries, setSmsCountries] = useState<Array<string>>([]);
  const [enableSms, setEnableSms] = useState<boolean>(true);
  const [enableOrderPrint, setEnableOrderPrint] = useState<boolean>(false);
  const [isNewCabinet, setIsNewCabinet] = useState<boolean>(false);
  const [deviceSerial, setDeviceSerial] = useState<string>('CHANGE ME');
  const [movingCabinet, setMovingCabinet] = useState<boolean>(false);
  const [cabinetBrand, setCabinetBrand] = useState<string>('');
  const [orderManagerCode, setOrderManagerCode] = useState<string>('008');
  const [orderManagerQrCode, setOrderManagerQrCode] = useState<string>('');
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [hasLighting, setHasLighting] = useState<boolean>(false);
  const [lightingColorOpen, setLightingColorOpen] = useState<string>('#ffffff');
  const [hasLightingColorOpenError, setHasLightingColorOpenError] = useState<boolean>(false);
  const [lightingColorOpenErrorText, setLightingColorOpenErrorText] = useState<string>('');
  const [lightingColorClosed, setLightingColorClosed] = useState<string>('#ffffff');
  const [hasLightingColorClosedError, setHasLightingColorClosedError] = useState<boolean>(false);
  const [lightingColorClosedErrorText, setLightingColorClosedErrorText] = useState<string>('');
  const [heatingEnabled, setHeatingEnabled] = useState<boolean>(false);
  const [temperature, setTemperature] = useState<string>('');
  const [temperatureUnit, setTemperatureUnit] = useState<string>('fahrenheit');
  const [heaterRulesEnabled, setHeaterRulesEnabled] = useState<boolean>(false);
  const [heaterRulesCustom, setHeaterRulesCustom] = useState<boolean>(false);
  const [heaterRules, setHeaterRules] = useState<Array<{mt: number, sp: number, disable?: boolean}>>([
    { mt: 10, sp: 110 },
    { mt: 30, sp: 100 },
    { mt: 60, sp: 90 },
    { mt: 60, sp: 0, disable: true }
  ]);
  const [enableTransferToCounter, setEnableTransferToCounter] = useState<boolean>(false);
  const [alwaysTransferToCounter, setAlwaysTransferToCounter] = useState<boolean>(false);
  const [expirePickups, setExpirePickups] = useState<boolean>(false);
  const [crewHome, setCrewHome] = useState<boolean>(false);
  const [pickupHome, setPickupHome] = useState<boolean>(false);
  const [queueHoldMinutes, setQueueHoldMinutes] = useState<string>('60');
  const [cabinetHoldMinutes, setCabinetHoldMinutes] = useState<string>('30');
  const [replacingSerial, setReplacingSerial] = useState<boolean>(false);
  const [newDeviceSerial, setNewDeviceSerial] = useState<string>('');
  const [newSerialErrorText, setNewSerialErrorText] = useState<string>('');
  const [hasNewSerialError, setHasNewSerialError] = useState<boolean>(false);
  const [showReplaceConfirmDialog, setShowReplaceConfirmDialog] = useState<boolean>(false);
  const [tabletWasReplaced, setTabletWasReplaced] = useState<boolean | null>(null);
  const [tabletIsInoperable, setTabletIsInoperable] = useState<boolean | null>(null);
  const [topologyType, setTopologyType] = useState<CabinetTopology>('single_tablet_external');
  const [topologyDevices, setTopologyDevices] = useState<DeviceRow[]>([]);
  const [topologyClaimWarnings, setTopologyClaimWarnings] = useState<Record<string, string>>({});
  const [topologyStatus, setTopologyStatus] = useState<string>('');
  const [topologySaving, setTopologySaving] = useState<boolean>(false);

  // Extracts the device list out of the topology response, whatever the wrapper shape.
  const extractDeviceList = (data: unknown): Array<Record<string, unknown>> => {
    if (!data) {
      return [];
    }
    const candidates: unknown[] = [
      data,
      (data as { desired?: unknown }).desired,
      (data as { devices?: unknown }).devices,
      ((data as { desired?: { devices?: unknown } }).desired || {}).devices,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate as Array<Record<string, unknown>>;
      }
      if (candidate && Array.isArray((candidate as { devices?: unknown }).devices)) {
        return (candidate as { devices: unknown[] }).devices as Array<Record<string, unknown>>;
      }
    }
    return [];
  };

  const truthy = (v: unknown) => v === true || v === 'true' || v === 1;

  // Parses GET /cabinets/{id}/topology device rows. Roles come back as the
  // desired_* fields; screen_role ('pickup' = courier/pickup, 'management' = crew)
  // is a fallback.
  const parseTopologyRoster = (data: unknown): DeviceRow[] => {
    return extractDeviceList(data)
      .map((d) => ({
        serial: String(d.serial ?? d.serial_number ?? ''),
        isPrimary: truthy(d.desired_is_primary) || truthy(d.is_primary) || d.desired_screen_role === 'pickup' || d.screen_role === 'pickup',
        isMaster: truthy(d.desired_is_master) || truthy(d.is_master),
      }))
      .filter((d) => d.serial.length > 0);
  };

  const loadTopology = async (cabinet: Cabinet) => {
    const response = await getCabinetTopology(store.getState().authorization.cloudKey, cabinet.id);
    const data = response?.data;

    const roster = parseTopologyRoster(data);
    const desired = (data?.desired ?? data) as { topology_type?: string } | undefined;
    const typeFromResponse = desired?.topology_type ?? (data as { topology_type?: string })?.topology_type;
    // Prefer the server's topology_type; otherwise infer multi when a roster exists.
    const type = (typeFromResponse || cabinet.topology || (roster.length ? 'multi_tablet' : 'single_tablet_external')) as CabinetTopology;
    setTopologyType(type);

    if (roster.length) {
      setTopologyDevices(roster);
    } else if (type === 'multi_tablet') {
      // Multi-tablet cabinet with no readable roster — seed a starter row.
      setTopologyDevices([{ serial: '', isPrimary: true, isMaster: true }]);
    } else {
      setTopologyDevices([]);
    }
  };

  const handleTopologyTypeChange = (type: CabinetTopology) => {
    setTopologyType(type);
    setTopologyStatus('');
    if (type === 'multi_tablet' && topologyDevices.length === 0) {
      setTopologyDevices([{ serial: '', isPrimary: true, isMaster: true }]);
    }
  };

  // Warn (non-blocking) if a serial is claimed by a DIFFERENT cabinet.
  const checkTopologySerialClaim = async (serial: string) => {
    const trimmed = serial.trim();
    if (!trimmed) {
      return;
    }
    const response = await lookupDeviceTopology(store.getState().authorization.cloudKey, trimmed);
    const data = response?.data;
    const otherClaimants: string[] = (data?.cabinet_ids || []).filter((id: string) => id !== cabinetId);
    setTopologyClaimWarnings((prev) => {
      const next = { ...prev };
      if (data?.claimed && otherClaimants.length) {
        next[trimmed] = `Already claimed by ${otherClaimants.join(', ')}`;
      } else {
        delete next[trimmed];
      }
      return next;
    });
  };

  const saveTopology = async () => {
    if (!isDeviceRosterValid(topologyType, topologyDevices)) {
      alert('Topology requires exactly one Master device (Courier/Pickup + Master) and at least one Crew device, with all serials filled.');
      return;
    }

    const roster: CabinetDevicePayload[] = topologyType === 'multi_tablet'
      ? topologyDevices.map((d) => ({
        serial: d.serial.trim(),
        is_master: d.isMaster,
        is_primary: d.isPrimary,
        screen_role: d.isPrimary ? 'pickup' : 'management',
      }))
      : [];

    setTopologySaving(true);
    const response = await updateCabinetTopology(store.getState().authorization.cloudKey, cabinetId, topologyType, roster);
    setTopologySaving(false);

    const status = response?.status ?? response?.response?.status;
    if (status === 409) {
      alert(`Topology not saved — a device serial is claimed by another cabinet.\n${response?.response?.data?.error ?? ''}`);
      return;
    }
    if (status === 400) {
      alert(`Topology not saved — ${response?.response?.data?.error ?? 'the roster must have exactly one master.'}`);
      return;
    }
    if (status !== 200 && status !== 201) {
      alert(`Topology not saved — ${response?.response?.data?.error ?? 'unknown error'}`);
      return;
    }

    // Success — surface MDM provisioning issues and any removed serials.
    const data = response.data || {};
    const messages: string[] = ['Topology saved.'];
    if (Array.isArray(data.mdm)) {
      const failed = data.mdm.filter((m: { success: boolean }) => m.success === false);
      failed.forEach((f: { serial: string, error?: string }) => messages.push(`Device ${f.serial}: ${f.error ?? 'provisioning failed'}`));
    }
    if (Array.isArray(data.removed_serials) && data.removed_serials.length) {
      messages.push(`Removed serials: ${data.removed_serials.join(', ')}`);
    }
    setTopologyStatus(messages.join('\n'));
  };

  const upsertEditedCabinet = async() => {
    if (!cabinetObject) {
      return;
    }
    cabinetObject.pucs = hardwareConfig;
    
    if (intermediateStoreId && !hasStoreIdError) {
      cabinetObject.store_id = intermediateStoreId;
    }
    
    const payload = Cabinet.payload(cabinetObject);
    if (orderManagerCode) {
      // Remove any existing 'display_order_manager' codes
      if (payload.properties.app.passwords.codes) {
        const codeObjects = Object.entries(payload.properties.app.passwords.codes).map( (k) => {
          return(k);
        });

        payload.properties.app.passwords.codes = {};
        codeObjects.forEach( (oneCode) => {
          // add new `display_order_manager` code
          if (oneCode[1] !== "display_order_manager") {
            payload.properties.app.passwords.codes[oneCode[0]] = oneCode[1];
          }
        });
      }

      payload.properties.app.passwords.codes[orderManagerCode] = 'display_order_manager';
    }

    if (orderManagerQrCode) {
      // Remove any existing 'display_order_manager' codes
      if (payload.properties.app.passwords.barcodes) {
        const codeObjects = Object.entries(payload.properties.app.passwords.barcodes).map( (k) => {
          return(k);
        });

        payload.properties.app.passwords.barcodes = {};
        codeObjects.forEach( (oneCode) => {
          // add new `display_order_manager` code
          if (oneCode[1] !== "display_order_manager") {
            payload.properties.app.passwords.codes[oneCode[0]] = oneCode[1];
          }
        });
      }

      payload.properties.app.passwords.barcodes[orderManagerQrCode] = 'display_order_manager';
    }

    if (queueHoldMinutes) {
      // Set timeout_queue from queueHoldMinutes value
      // Displayed value is in minutes; stored value is in seconds
      payload.properties.app.order.timeout_queue = (parseInt(queueHoldMinutes) * 60).toString();
    }

    if (cabinetHoldMinutes) {
      // Set timeout_cabinet from cabinetHoldMinutes value
      // Displayed value is in minutes; stored value is in seconds
      payload.properties.app.order.timeout_cabinet = (parseInt(cabinetHoldMinutes) * 60).toString();
    }

    delete payload.properties.app.ui_assets_s3_url;
    delete payload.properties.app.store_city;
    delete payload.properties.app.store_properties;
    delete payload.properties.app.locale;
    delete payload.properties.app.mode;
    delete payload.properties.app.displays;
    delete payload.properties.app.organization_order_sources;
    delete payload.properties.app.order.code_start;
    delete payload.properties.app.order.code_end;

    // If cabinet has lighting, append the default lighting parameters
    if (hasLighting) {
      const lightingParams = {
        "lighting": {
          "color_open": lightingColorOpen,
          "color_closed": lightingColorClosed,
          "fade_time_ms": 2000,
          "leds_per_locker": 30
        }
      };
      payload.properties = { ...payload.properties, ...lightingParams};
    } else {
      // if no lighting, remove the lighting object
      delete payload.properties.lighting;
    }

    // Ensure hardware.global exists
    if (!payload.properties.hardware) {
      payload.properties.hardware = { global: {} };
    }
    if (!payload.properties.hardware.global) {
      payload.properties.hardware.global = {};
    }

    // Helper: convert F to C for storage
    const fToC = (f: number) => Math.round((f - 32) * 5 / 9);

    if (heatingEnabled) {
      const tempVal = parseInt(temperature) || 0;
      const tempInCelsius = temperatureUnit === 'fahrenheit' ? fToC(tempVal) : tempVal;
      if (tempInCelsius <= 0) {
        const minLabel = temperatureUnit === 'fahrenheit' ? '32°F' : '0°C';
        alert(`Temperature setpoint must be above ${minLabel}.`);
        return;
      }
      payload.properties.hardware.global.lockers_heat_enable = 'on';
      // Always store in Celsius
      payload.properties.hardware.global.lockers_heat_set = tempInCelsius;
      payload.properties.app = { ...payload.properties.app, units: { temperature: temperatureUnit } };
    } else {
      payload.properties.hardware.global.lockers_heat_enable = 'off';
      delete payload.properties.hardware.global.lockers_heat_set;
      delete payload.properties.app.units;
    }

    // Handle heater setpoint rules
    if (heatingEnabled && heaterRulesEnabled) {
      if (heaterRulesCustom && heaterRules.length > 0) {
        // Always store rules in Celsius
        const rulesToSave = temperatureUnit === 'fahrenheit'
          ? heaterRules.map(r => ({
              ...r,
              mt: fToC(r.mt),
              sp: r.disable ? 0 : fToC(r.sp)
            }))
          : heaterRules;
        payload.properties.hardware.global.heater_setpoint_rules = {
          enabled: true,
          rules: rulesToSave
        };
      } else {
        payload.properties.hardware.global.heater_setpoint_rules = {
          enabled: true
        };
      }
    } else {
      delete payload.properties.hardware.global.heater_setpoint_rules;
    }

    // Clean up: remove hardware entirely if global is empty
    if (Object.keys(payload.properties.hardware.global).length === 0) {
      delete payload.properties.hardware;
    }

    payload.properties.app.crew_home = (!crewHome ? 'manage' : 'create_new');
    payload.properties.app.pickup_home = (!pickupHome ? 'start' : 'select_pickup');
    payload.properties.app.expire_pickups = expirePickups;
    payload.properties.app.enable_transfer_to_counter = enableTransferToCounter;
    payload.properties.app.always_transfer_to_counter = alwaysTransferToCounter;

    const response = await upsertCabinet(store.getState().authorization.cloudKey, payload);
    if (response?.status !== 201) {
      const details = response.response.data.details ?? {};
      const isHeatSetError = Object.values(details).some((v) => String(v).includes('lockers_heat_set'));
      const heatSetValue = payload.properties.hardware?.global?.lockers_heat_set;
      if (isHeatSetError && heatSetValue != null && heatSetValue <= 0) {
        const minLabel = temperatureUnit === 'fahrenheit' ? '32°F' : '0°C';
        alert(`Temperature setpoint must be above ${minLabel}.`);
      } else {
        let detailString = '';
        Object.keys(details).forEach(k => { detailString += details[k] + '\n'; });
        alert(`\n${response.response.data.error}:\n${detailString}`);
      }
    } else {
      if (isNewCabinet) {
        const deviceResponse = await putCabinetDevice(store.getState().authorization.cloudKey, cabinetId, description, deviceSerial);
        if (deviceResponse?.status !== 201) {
          let detailString = '';
          Object.keys(deviceResponse.response.data.details).forEach(k => {
            detailString += deviceResponse.response.data.details[k];
          });
          alert(`\n${deviceResponse.response.data.error}:\n${detailString}`);
        }
      }
      refresh();
      setDialog(false);
    }
  };

  const handleConfirmReplaceSerial = async () => {
    setShowReplaceConfirmDialog(false);

    const deleteResponse = await deleteCabinetDevice(store.getState().authorization.cloudKey, cabinetId);
    const deleteStatus = deleteResponse?.status ?? deleteResponse?.response?.status;
    if (deleteStatus !== 200 && deleteStatus !== 204 && deleteStatus !== 404) {
      alert(`Failed to remove old device: ${deleteResponse?.response?.data?.error}`);
      setReplacingSerial(false);
      return;
    }

    const putResponse = await putCabinetDevice(store.getState().authorization.cloudKey, cabinetId, description, newDeviceSerial);
    if (putResponse?.status !== 201) {
      alert('Old device removed but new serial could not be registered.\nPlease re-enter the new serial manually.');
      setReplacingSerial(false);
      return;
    }

    setReplacingSerial(false);
    await upsertEditedCabinet();
  };

  const handleReplacingSerial = () => {
    setNewDeviceSerial('');
    setNewSerialErrorText('');
    setHasNewSerialError(false);
    setTabletWasReplaced(null);
    setTabletIsInoperable(null);
    setReplacingSerial(!replacingSerial);
  };

  const setCabinetData = (cabinet: Cabinet) => {
    setCabinetObject(cabinet);
    setStoreId(cabinet.store_id);
    setIntermediateStoreId(cabinet.store_id);
    setCabinetId(cabinet.id);
    setDescription(cabinet.description);
    setBusinessName(cabinet.business_name || '');
    setBusinessAddress(cabinet.business_address || '');
    setBusinessPhone(cabinet.business_phone || '');
    setCabinetBrand(cabinet.properties.app.brand || 'carterhoffmann');
    setCabinetLocation(cabinet.cabinet_location || '');
    setHardwareConfig(cabinet.pucs);
    setSmsCountries(cabinet.sms_countries || ['US']);
    setEnableSms(cabinet.enable_order_sms!);
    setEnableOrderPrint(cabinet.enable_order_print!);
    setOrderManagerCode(Object.keys(cabinet.properties?.app?.passwords?.codes)[0]);
    setOrderManagerQrCode(Object.keys(cabinet.properties?.app?.passwords?.barcodes)[0]);
    // Value is stored in seconds. To display minutes. divide by 60.
    setQueueHoldMinutes((parseInt(cabinet.properties.app.order.timeout_queue) / 60).toString());
    setCabinetHoldMinutes((parseInt(cabinet.properties.app.order.timeout_cabinet) / 60).toString());
    // if lighting object is present hasLIghting is true; otherwise false
    const cabHasLighting = (cabinet.properties.lighting) ? true : false;
    setHasLighting(cabHasLighting);

    // If there is lighting, set the open and closed colors from cabinet properties
    let cabColorOpen = lightingColorOpen;

    if (cabHasLighting && cabinet.properties?.lighting?.color_open) {
      cabColorOpen = cabinet.properties.lighting.color_open;
    }
    setLightingColorOpen(cabColorOpen);

    let cabColorClosed = lightingColorClosed;

    if (cabHasLighting && cabinet.properties?.lighting?.color_closed) {
      cabColorClosed = cabinet.properties.lighting.color_closed;
    }
    setLightingColorClosed(cabColorClosed);

    // set the temp unit
    const displayUnit = cabinet.properties.app.units?.temperature || 'fahrenheit';
    setTemperatureUnit(displayUnit);

    // Helper: convert C to F for display
    const cToF = (c: number) => Math.round(c * 9 / 5 + 32);

    // Load heating state (DB values are always in Celsius)
    if (cabinet.properties.hardware) {
      setHeatingEnabled(cabinet.properties.hardware?.global?.lockers_heat_enable === 'on');
      if (cabinet.properties.hardware?.global?.lockers_heat_set != null) {
        const storedTemp = cabinet.properties.hardware.global.lockers_heat_set;
        setTemperature(displayUnit === 'fahrenheit' ? cToF(storedTemp).toString() : storedTemp.toString());
      }

      // Load heater setpoint rules (DB values are always in Celsius)
      const existingRules = cabinet.properties.hardware?.global?.heater_setpoint_rules;
      if (existingRules) {
        setHeaterRulesEnabled(existingRules.enabled === true);
        if (existingRules.rules && existingRules.rules.length > 0) {
          setHeaterRulesCustom(true);
          // Convert from Celsius to display unit if needed
          const loadedRules = displayUnit === 'fahrenheit'
            ? existingRules.rules.map((r: any) => ({
                ...r,
                mt: cToF(r.mt),
                sp: r.disable ? 0 : cToF(r.sp)
              }))
            : existingRules.rules;
          setHeaterRules(loadedRules);
        } else {
          setHeaterRulesCustom(false);
        }
      } else {
        setHeaterRulesEnabled(false);
        setHeaterRulesCustom(false);
      }
    }

    // Only want the code for display_order_manager, not for display_configuration
    const orderManagerCode = Object.keys(cabinet.properties?.app?.passwords?.codes).find( (key) => cabinet.properties?.app?.passwords?.codes[key] === 'display_order_manager');

    if (orderManagerCode) {
      setOrderManagerCode(orderManagerCode);
    }

    // OrderManagerQrCode for display_order_manager
    const orderManagerQrCode = Object
      .keys(cabinet.properties?.app?.passwords?.barcodes)
      .find(
        (key) => cabinet.properties?.app?.passwords?.barcodes[key] === 'display_order_manager'
      );

    if (orderManagerQrCode) {
      setOrderManagerQrCode(orderManagerQrCode);
    }

    setCrewHome(cabinet.properties?.app?.crew_home === 'manage' ? false : true);
    setPickupHome(cabinet.properties?.app?.pickup_home === 'start' ? false : true);
    setExpirePickups(cabinet.properties?.app?.expire_pickups || false);
    setEnableTransferToCounter(cabinet.properties?.app?.enable_transfer_to_counter || false);
    setAlwaysTransferToCounter(cabinet.properties?.app?.always_transfer_to_counter || false);
  };
  
  useEffect(() => {
    const fetchCabinet = async () => {
      const [fetchedCabinetConfig, allCabinets] = await Promise.all([
        getCabinetConfiguration(store.getState().authorization.cloudKey, editingCabinet),
        getCabinets(store.getState().authorization.cloudKey, '', selectedStoreId),
      ]);
      const concreteCabinet = new Cabinet(fetchedCabinetConfig.data);
      const cabinetWithDevices = allCabinets.data.find((c: ICabinet) => c.id === editingCabinet);
      if (cabinetWithDevices?.devices) {
        concreteCabinet.devices = cabinetWithDevices.devices;
      }
      setCabinetData(concreteCabinet);
      await loadTopology(concreteCabinet);
    };

    if (editingCabinet) {
      setIsNewCabinet(false);
      fetchCabinet();
    } else {
      setIsNewCabinet(true);
      setIsFormValid(false);
      const newCabinet = Cabinet.generateNewCabinet('', selectedStoreId);
      setCabinetData(newCabinet);
    }
  }, []);

  const applyHardwareChanges = (changedHardware: Array<Puc>) => {
    cabinetObject!.pucs = changedHardware;
    setHardwareConfig(changedHardware);
  };

  const changeSmsCountries = (event: SelectChangeEvent<typeof smsCountries>) => {
    const {
      target: { value },
    } = event;
    const currentList = typeof value === 'string' ? value.split(',') : value;
    cabinetObject!.sms_countries = currentList;
    setSmsCountries(currentList);
  };
  
  const renderDeviceSerial = () => {
    if (isNewCabinet) {
      return <TextField
        inputProps={{ maxLength: 16 }}
        sx={{
          width: '350px',
          marginTop: '10px',
          marginBottom: '25px',
        }}
        label='ELO Device Serial'
        value={deviceSerial}
        onChange={(e) => {
          if (!e.target.value) {
            alert('New cabinets require an ELO serial');
            setIsFormValid(false);
          } else {
            setIsFormValid(true);
          }
          setDeviceSerial(e.target.value);
        }} />;
    } else {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px', marginBottom: '25px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <TextField
              sx={{ width: '350px' }}
              label='ELO Device Serial'
              value={cabinetObject?.getTabletSerial() ?? ''}
              disabled />
            <Button
              variant='contained'
              onClick={handleReplacingSerial}>
              {replacingSerial ? 'Cancel' : 'Edit Serial'}
            </Button>
          </div>
          {replacingSerial && (
            <TextField
              inputProps={{ maxLength: 16 }}
              sx={{ width: '350px', marginTop: '12px' }}
              label='New ELO Device Serial'
              value={newDeviceSerial}
              error={hasNewSerialError}
              helperText={newSerialErrorText}
              onChange={(e) => {
                const val = e.target.value;
                setNewDeviceSerial(val);
                if (!val) {
                  setHasNewSerialError(true);
                  setNewSerialErrorText('New serial is required');
                } else if (val === cabinetObject?.getTabletSerial()) {
                  setHasNewSerialError(true);
                  setNewSerialErrorText('New serial must differ from current serial');
                } else {
                  setHasNewSerialError(false);
                  setNewSerialErrorText('');
                }
              }} />
          )}
        </div>
      );
    }
  };

  const handleMovingCabinet = () => {
    setIntermediateStoreId(storeId);
    setHasStoreIdError(false);
    setStoreIdErrorText('');
    setMovingCabinet(!movingCabinet);
  };

  const validate = () => {
    const invalidDetails = !deviceSerial || !description || !businessName || !businessAddress || !businessPhone || !cabinetLocation || !orderManagerCode || !cabinetBrand || movingCabinet;
    if (invalidDetails) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };

  useEffect(() => {
    validate();
  }, [deviceSerial, description, businessAddress, businessName, businessPhone, cabinetLocation, cabinetBrand, orderManagerCode, movingCabinet]);

  return (
    <Box height='100%'>
      <DialogTitle
        display='flex'
        flexDirection='row'
        alignItems='center'
        sx={{
          height: '100px',
          width: '100%'
        }}>
        <div style={{
            fontWeight: '500',
            color: '#121F35',
            fontSize: '40px',
            marginRight: '100px'
          }}>
          {'Cabinet Editor'}
        </div>
        <div>
          <Button
            variant='contained'
            sx={{
              width: '100px',
              fontSize: '24px',
              marginRight: '25px'
            }}
            disabled={!isFormValid || (replacingSerial && (hasNewSerialError || !newDeviceSerial))}
            onClick={async() => {
              if (!isNewCabinet && replacingSerial) {
                setTabletWasReplaced(null);
                setTabletIsInoperable(null);
                setShowReplaceConfirmDialog(true);
              } else {
                await upsertEditedCabinet();
              }
            }}>
            Save
          </Button>
          <Button
            variant='contained'
            sx={{
              width: '100px',
              fontSize: '24px'
            }}
            onClick={() => {
              setDialog(false);
            }}>
            Cancel
          </Button>
        </div>
      </DialogTitle>
      <DialogContent
        sx={{
          height: '650px'
        }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
              <Tab label="Cabinet Settings" {...a11yProps(0)} />
              <Tab label="Cabinet Details" {...a11yProps(1)} />
              <Tab label="Cabinet Hardware" {...a11yProps(2)} />
              <Tab label="Heater Settings" {...a11yProps(3)} />
              <Tab label="Topology" {...a11yProps(4)} />
            </Tabs>
          </Box>
          <CabinetTabPanel
            value={value}
            index={0}>
            <Box 
              sx={{
                height: '300px',
                width: '1000px',
                display: 'flex',
                flexDirection: 'column',
              }}>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%'
              }}>
                <TextField
                  inputProps={{ maxLength: 24 }}
                  sx={{
                    width: '350px',
                    marginTop: '10px',
                    marginBottom: '25px',
                    marginRight: '24px',
                  }}
                  disabled
                  label='ID'
                  value={cabinetId} />
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  }}>
                  <TextField
                    error={hasStoreIdError}
                    helperText={storeIdErrorText}
                    sx={{
                      width: '350px',
                      marginTop: '10px',
                      marginBottom: '25px',
                      marginLeft: '24px',
                    }}
                    disabled={!movingCabinet}
                    label='Store ID'
                    value={intermediateStoreId}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setStoreIdErrorText('Invalid store ID to move cabinet');
                        setHasStoreIdError(true);
                      } else {
                        setStoreIdErrorText('');
                        setHasStoreIdError(false);
                      }
                    }}
                    onChange = {(e) => {
                      if (!stores.find(x => x === e.target.value)) {
                        setStoreIdErrorText('Invalid store ID to move cabinet');
                        setHasStoreIdError(true);
                        setIsFormValid(false);
                      } else {
                        setStoreIdErrorText('');
                        setHasStoreIdError(false);
                        setIsFormValid(true);
                      }
                      setIntermediateStoreId(e.target.value);
                    }} />
                  <Button
                    variant='contained'
                    sx={{
                      width: '200px',
                      height: '60px',
                      fontSize: '20px',
                      marginLeft: '20px',
                      marginTop: '8px',
                    }}
                    onClick={handleMovingCabinet}>
                    {movingCabinet
                      ? 'Cancel'
                      : 'Move Cabinet'}
                  </Button>
                </div>
              </div>
              {renderDeviceSerial()}

              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}>
                <div style={{
                  display: 'flex'
                }}>
                  <Typography>Lighting</Typography>
                  <Switch
                    checked={hasLighting}
                    onChange={(e) => {
                      setHasLighting(e.target.checked);
                  }} />

                  <TextField 
                    sx={{
                      width: '190px',
                      marginRight: '8px'
                    }}
                    error={hasLightingColorOpenError}
                    helperText={lightingColorOpenErrorText}
                    disabled={!hasLighting}
                    required={hasLighting}
                    label="Lighting Color Open"
                    value={lightingColorOpen}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setLightingColorOpenErrorText('Lighting Color Open is required');
                        setHasLightingColorOpenError(true);
                      } else {
                        setLightingColorOpenErrorText('');
                        setHasLightingColorOpenError(false);
                      }
                    }}
                    onChange={(e) => {
                      setLightingColorOpen(e.target.value);
                    }} />

                  <TextField 
                    sx={{
                      width: '190px',
                      marginLeft: '8px'
                    }}
                    error={hasLightingColorClosedError}
                    helperText={lightingColorClosedErrorText}
                    disabled={!hasLighting}
                    required={hasLighting}
                    label="Lighting Color Closed"
                    value={lightingColorClosed}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setLightingColorClosedErrorText('Lighting Color Closed is required');
                        setHasLightingColorClosedError(true);
                      } else {
                        setLightingColorClosedErrorText('');
                        setHasLightingColorClosedError(false);
                      }
                    }}
                    onChange={(e) => {
                      setLightingColorClosed(e.target.value);
                    }} />
                </div>
              </div>

              <div style={{
                width: '35%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div>
                  <Typography>Enable Order Print</Typography>
                  <Switch
                    checked={enableOrderPrint}
                    onChange={(e) => {
                      cabinetObject!.enable_order_print = e.target.checked;
                      setEnableOrderPrint(e.target.checked);
                    }} />
                </div>
                
                <div>
                  <Typography>ExpirePickups</Typography>
                  <Switch
                    checked={expirePickups}
                    onChange={(e) => {
                      setExpirePickups(e.target.checked);
                  }} />
                </div>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '33%'
                }}>
                  <Typography>Enable SMS</Typography>
                  <Switch
                    checked={enableSms}
                    onChange={(e) => {
                      cabinetObject!.enable_order_sms = e.target.checked;
                      setEnableSms(e.target.checked);
                    }} />
                </div>

                <div style={{
                  width: '100%'
                }}>
                  <Typography>SMS COUNTRIES</Typography>
                  <Select sx={{
                    width: '40%'
                  }}
                    multiple
                    value={smsCountries}
                    onChange={changeSmsCountries}
                    input={<OutlinedInput label="Country" />}
                    MenuProps={MenuProps}>
                    {allCountries.map((country) => (
                      <MenuItem
                        key={country.iso2}
                        value={country.iso2}
                        style={getStyles(country.iso2, smsCountries, theme)}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>

              <div style={{
                width: '45%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div>
                  <Typography>Enable Transfer to Counter</Typography>
                  <Switch
                    checked={enableTransferToCounter}
                    onChange={(e) => {
                      cabinetObject!.enable_order_print = e.target.checked;
                      setEnableTransferToCounter(e.target.checked);
                    }} />
                </div>
                
                <div>
                  <Typography>Always Transfer to Counter</Typography>
                  <Switch
                    checked={alwaysTransferToCounter}
                    onChange={(e) => {
                      setAlwaysTransferToCounter(e.target.checked);
                  }} />
                </div>
              </div>

              <div style={{
                width: '45%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div>
                  <Typography>Crew Home Screen</Typography>
                  <div style={{
                    display: 'flex',
                  }}>
                    <Typography style={{
                      marginTop: '8px'
                    }}>Manage</Typography>
                    <Switch 
                      checked={crewHome}
                      onChange={(e) => {
                        setCrewHome(e.target.checked);
                      }} />
                    <Typography style={{
                      marginTop: '8px'
                    }}>Create New</Typography>
                  </div>
                </div>
                
                <div>
                  <Typography>Pickup Home Screen</Typography>
                  <div style={{
                    display: 'flex',
                  }}>
                    <Typography style={{
                      marginTop: '8px'
                    }}>Home</Typography>
                    <Switch 
                      checked={pickupHome}
                      onChange={(e) => {
                        setPickupHome(e.target.checked);
                      }} />
                    <Typography style={{
                      marginTop: '8px'
                    }}>Select Pickup</Typography>
                  </div>
                </div>
              </div>
            </Box>
          </CabinetTabPanel>
          <CabinetTabPanel value={value} index={1}>
            <Box
              sx={{
                height: '300px',
                width: '50%',
                display: 'inline-flex',
                flexDirection: 'column',
              }}>
              <TextField
                error={hasDescriptionError}
                helperText={descriptionErrorText}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label='Description'
                required
                value={description}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setDescriptionErrorText('Description is required');
                    setHasDescriptionError(true);
                  } else {
                    setDescriptionErrorText('');
                    setHasDescriptionError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.description = e.target.value;
                  setDescription(e.target.value);
                }} />
              <TextField
                error={hasBusinessNameError}
                helperText={businessNameErrorText}
                inputProps={{ maxLength: 50 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label='Business Name'
                value={businessName}
                required
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBusinessNameErrorText('Business Name is required');
                    setHasBusinessNameError(true);
                  } else {
                    setBusinessNameErrorText('');
                    setHasBusinessNameError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.business_name = e.target.value;
                  setBusinessName(e.target.value);
                }} />
              <TextField
                error={hasBusinessAddressError}
                helperText={businessAddressErrorText}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Business Address'
                value={businessAddress}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBusinessAddressErrorText('Business Address is required');
                    setHasBusinessAddressError(true);
                  } else {
                    setBusinessAddressErrorText('');
                    setHasBusinessAddressError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.business_address = e.target.value;
                  setBusinessAddress(e.target.value);
                }} />
              <TextField
                error={hasBusinessPhoneError}
                helperText={businessPhoneErrorText}
                inputProps={{ maxLength: 15 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Business Phone'
                value={businessPhone}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBusinessPhoneErrorText('Business Phone is required');
                    setHasBusinessPhoneError(true);
                  } else {
                    setBusinessPhoneErrorText('');
                    setHasBusinessPhoneError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.business_phone = e.target.value;
                  setBusinessPhone(e.target.value);
                }} />
              <TextField
                error={hasCabinetLocationError}
                helperText={cabinetLocationErrorText}
                inputProps={{ maxLength: 75 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Location'
                value={cabinetLocation}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setCabinetLocationErrorText('Location is required');
                    setHasCabinetLocationError(true);
                  } else {
                    setCabinetLocationErrorText('');
                    setHasCabinetLocationError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.cabinet_location = e.target.value;
                  setCabinetLocation(e.target.value);
                }} />
            </Box>
            <Box
            
              sx={{
                height: '300px',
                width: '50%',
                display: 'inline-flex',
                flexDirection: 'column',
              }}>
              <TextField
                error={hasBrandError}
                helperText={brandErrorText}
                inputProps={{ maxLength: 36 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Brand'
                value={cabinetBrand}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBrandErrorText('Brand is required');
                    setHasBrandError(true);
                  } else {
                    setBrandErrorText('');
                    setHasBrandError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.properties.app.brand = e.target.value;
                  setCabinetBrand(e.target.value);
                }} />
              <TextField
                error={hasOrderManagerCodeError}
                helperText={orderManagerCodeErrorText}
                inputProps={{ maxLength: 36 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Order Manager Code'
                value={orderManagerCode}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setOrderManagerCodeErrorText('Order ManagerCode is required');
                    setHasOrderManagerCodeError(true);
                  } else {
                    setOrderManagerCodeErrorText('');
                    setHasOrderManagerCodeError(false);
                  }
                }}
                onChange = {(e) => {
                  setOrderManagerCode(e.target.value);
                }} />
              <TextField
                inputProps={{ maxLength: 36 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label='Order Manager QR Code'
                value={orderManagerQrCode}
                onChange = {(e) => {
                  setOrderManagerQrCode(e.target.value);
                }} />
              <TextField
                inputProps={{ maxLength: 10 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label="Queue Hold Time (minutes)"
                value={queueHoldMinutes}
                onChange = {(e) => {
                  setQueueHoldMinutes(e.target.value);
                }} />
              <TextField
                inputProps={{ maxLength: 10 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label="Cabinet Hold Time (minutes)"
                value={cabinetHoldMinutes}
                onChange = {(e) => {
                  setCabinetHoldMinutes(e.target.value);
                }} />
            </Box>
          </CabinetTabPanel>
          <CabinetTabPanel value={value} index={2}>
            <PucEditor
              pucs={hardwareConfig}
              applyHardwareChanges={applyHardwareChanges}
              canvasWidth={776}
              canvasHeight={540} />
          </CabinetTabPanel>
          <CabinetTabPanel value={value} index={3}>
            <Box sx={{ maxWidth: 600 }}>
              {/* Section 1: Heating */}
              <Typography variant="h6" sx={{ mb: 2 }}>Heating</Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ mr: 2 }}>Enable Heating</Typography>
                <Switch
                  checked={heatingEnabled}
                  onChange={(e) => {
                    setHeatingEnabled(e.target.checked);
                    if (!e.target.checked) {
                      setHeaterRulesEnabled(false);
                    }
                  }} />
              </Box>

              {heatingEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <TextField
                    sx={{ width: '120px' }}
                    type="number"
                    label="Setpoint"
                    value={temperature}
                    onChange={(e) => {
                      setTemperature(e.target.value);
                    }} />
                  <ToggleButtonGroup
                    value={temperatureUnit}
                    exclusive
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 2,
                        py: 0.75,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          borderColor: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      },
                    }}
                    onChange={(_e, newUnit) => {
                      if (newUnit !== null) {
                        // Convert setpoint
                        const currentVal = parseInt(temperature) || 0;
                        if (newUnit === 'celsius') {
                          setTemperature(Math.round((currentVal - 32) * 5 / 9).toString());
                        } else {
                          setTemperature(Math.round(currentVal * 9 / 5 + 32).toString());
                        }
                        // Convert heater rules
                        setHeaterRules(heaterRules.map(r => {
                          if (newUnit === 'celsius') {
                            return { ...r, mt: Math.round((r.mt - 32) * 5 / 9), sp: r.disable ? 0 : Math.round((r.sp - 32) * 5 / 9) };
                          } else {
                            return { ...r, mt: Math.round(r.mt * 9 / 5 + 32), sp: r.disable ? 0 : Math.round(r.sp * 9 / 5 + 32) };
                          }
                        }));
                        setTemperatureUnit(newUnit);
                      }
                    }}>
                    <ToggleButton value="fahrenheit">°F</ToggleButton>
                    <ToggleButton value="celsius">°C</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Section 2: Weather-Based Heater Rules */}
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Weather-Based Heater Rules</Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Typography sx={{ mr: 2 }}>Weather-Based Heater Control</Typography>
                  <Switch
                    checked={heaterRulesEnabled}
                    disabled={!heatingEnabled}
                    onChange={(e) => setHeaterRulesEnabled(e.target.checked)}
                  />
                  <Typography variant="body2" color={heaterRulesEnabled && heatingEnabled ? 'success.main' : 'text.secondary'}>
                    {heaterRulesEnabled && heatingEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>

                {!heatingEnabled && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Enable heating above to configure weather-based rules.
                  </Typography>
                )}

                {heatingEnabled && heaterRulesEnabled && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ mr: 2 }}>Use Custom Rules</Typography>
                      <Switch
                        checked={heaterRulesCustom}
                        onChange={(e) => setHeaterRulesCustom(e.target.checked)}
                      />
                    </Box>

                    {/* Default rules display */}
                    {!heaterRulesCustom && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Default Rules (hardcoded on device):</Typography>
                        {temperatureUnit === 'fahrenheit' ? (
                          <>
                            <Typography variant="body2">Below 10°F → 110°F</Typography>
                            <Typography variant="body2">10°F – 30°F → 100°F</Typography>
                            <Typography variant="body2">30°F – 60°F → 90°F</Typography>
                            <Typography variant="body2">Above 60°F → OFF</Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">Below {Math.round((10 - 32) * 5 / 9)}°C → {Math.round((110 - 32) * 5 / 9)}°C</Typography>
                            <Typography variant="body2">{Math.round((10 - 32) * 5 / 9)}°C – {Math.round((30 - 32) * 5 / 9)}°C → {Math.round((100 - 32) * 5 / 9)}°C</Typography>
                            <Typography variant="body2">{Math.round((30 - 32) * 5 / 9)}°C – {Math.round((60 - 32) * 5 / 9)}°C → {Math.round((90 - 32) * 5 / 9)}°C</Typography>
                            <Typography variant="body2">Above {Math.round((60 - 32) * 5 / 9)}°C → OFF</Typography>
                          </>
                        )}
                      </Box>
                    )}

                    {/* Custom rules editor */}
                    {heaterRulesCustom && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Custom Rules:</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                          All values in °{temperatureUnit === 'fahrenheit' ? 'F' : 'C'}
                        </Typography>
                        {heaterRules.map((rule, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                            {rule.disable ? (
                              <>
                                <TextField
                                  label={`Off Above (°${temperatureUnit === 'fahrenheit' ? 'F' : 'C'})`}
                                  type="number"
                                  size="small"
                                  sx={{ width: 130 }}
                                  value={rule.mt}
                                  onChange={(e) => {
                                    const updated = [...heaterRules];
                                    updated[index] = { ...updated[index], mt: parseInt(e.target.value) || 0 };
                                    setHeaterRules(updated);
                                  }}
                                />
                                <TextField
                                  label={`Setpoint (°${temperatureUnit === 'fahrenheit' ? 'F' : 'C'})`}
                                  type="number"
                                  size="small"
                                  sx={{ width: 130 }}
                                  value={0}
                                  disabled
                                />
                                <Typography variant="body2" sx={{ minWidth: 60, color: 'error.main', fontWeight: 500 }}>
                                  OFF
                                </Typography>
                              </>
                            ) : (
                              <>
                                <TextField
                                  label={`Max Temp (°${temperatureUnit === 'fahrenheit' ? 'F' : 'C'})`}
                                  type="number"
                                  size="small"
                                  sx={{ width: 130 }}
                                  value={rule.mt}
                                  onChange={(e) => {
                                    const updated = [...heaterRules];
                                    updated[index] = { ...updated[index], mt: parseInt(e.target.value) || 0 };
                                    setHeaterRules(updated);
                                  }}
                                />
                                <TextField
                                  label={`Setpoint (°${temperatureUnit === 'fahrenheit' ? 'F' : 'C'})`}
                                  type="number"
                                  size="small"
                                  sx={{ width: 130 }}
                                  value={rule.sp}
                                  onChange={(e) => {
                                    const updated = [...heaterRules];
                                    updated[index] = { ...updated[index], sp: parseInt(e.target.value) || 0 };
                                    setHeaterRules(updated);
                                  }}
                                />
                                <Typography variant="body2" sx={{ minWidth: 60 }}>
                                  {rule.sp}°{temperatureUnit === 'fahrenheit' ? 'F' : 'C'}
                                </Typography>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const updated = heaterRules.filter((_, i) => i !== index);
                                    setHeaterRules(updated);
                                  }}
                                >
                                  Remove
                                </Button>
                              </>
                            )}
                          </Box>
                        ))}
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                          onClick={() => {
                            const updated = [...heaterRules];
                            const catchAll = updated.pop();
                            updated.push({ mt: 0, sp: temperatureUnit === 'fahrenheit' ? 80 : 27 });
                            if (catchAll) updated.push(catchAll);
                            setHeaterRules(updated);
                          }}
                        >
                          + Add Rule
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </CabinetTabPanel>
          <CabinetTabPanel value={value} index={4}>
            {isNewCabinet ? (
              <Typography>Topology can be configured after the cabinet is created.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <DeviceRosterFields
                  topology={topologyType}
                  onTopologyChange={handleTopologyTypeChange}
                  devices={topologyDevices}
                  onDevicesChange={(devices) => {
                    setTopologyStatus('');
                    setTopologyDevices(devices);
                  }}
                  serialClaimWarnings={topologyClaimWarnings}
                  onSerialBlur={checkTopologySerialClaim} />

                <Button
                  variant='contained'
                  sx={{ width: '200px', marginTop: '16px' }}
                  disabled={topologySaving || !isDeviceRosterValid(topologyType, topologyDevices)}
                  onClick={async () => { await saveTopology(); }}>
                  {topologySaving ? 'Saving…' : 'Save Topology'}
                </Button>

                {topologyStatus && (
                  <Typography sx={{ marginTop: '16px', whiteSpace: 'pre-line', color: 'text.secondary' }}>
                    {topologyStatus}
                  </Typography>
                )}
              </Box>
            )}
          </CabinetTabPanel>
        </Box>
      </DialogContent>

      <Dialog open={showReplaceConfirmDialog} onClose={() => setShowReplaceConfirmDialog(false)}>
        <DialogTitle>Replace ELO Tablet Serial?</DialogTitle>
        <DialogContent>
          {tabletWasReplaced === null ? (
            <DialogContentText>
              Has the ELO tablet been physically replaced?
            </DialogContentText>
          ) : tabletWasReplaced === true ? (
            <DialogContentText>
              {`The old tablet will be deactivated. New serial "${newDeviceSerial}" will be registered.`}
            </DialogContentText>
          ) : tabletIsInoperable === null ? (
            <DialogContentText>
              Has the old tablet completely stopped working and is inoperable?
            </DialogContentText>
          ) : tabletIsInoperable === true ? (
            <DialogContentText>
              {`New serial "${newDeviceSerial}" will be registered.`}
            </DialogContentText>
          ) : (
            <DialogContentText>
              Warning: Updating the serial without replacing the tablet will deactivate the old device. This action cannot be undone.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {tabletWasReplaced === null ? (
            <>
              <Button onClick={() => setShowReplaceConfirmDialog(false)}>Go Back</Button>
              <Button onClick={() => setTabletWasReplaced(false)}>No</Button>
              <Button onClick={() => setTabletWasReplaced(true)}>Yes</Button>
            </>
          ) : tabletWasReplaced === true ? (
            <>
              <Button onClick={() => setTabletWasReplaced(null)}>Go Back</Button>
              <Button color='primary' variant='contained' onClick={handleConfirmReplaceSerial}>Confirm</Button>
            </>
          ) : tabletIsInoperable === null ? (
            <>
              <Button onClick={() => setTabletWasReplaced(null)}>Go Back</Button>
              <Button onClick={() => setTabletIsInoperable(false)}>No</Button>
              <Button onClick={() => setTabletIsInoperable(true)}>Yes</Button>
            </>
          ) : (
            <>
              <Button onClick={() => setTabletIsInoperable(null)}>Go Back</Button>
              <Button
                color={tabletIsInoperable ? 'primary' : 'error'}
                variant='contained'
                onClick={handleConfirmReplaceSerial}>
                Confirm
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CabinetDialog;
